import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "react-query";
import { formsApi } from "../../services/api";
import {
  DocumentTextIcon,
  PlusIcon,
  EyeIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "../../contexts/TranslationContext";
import LoadingSpinner from "../common/LoadingSpinner";
import ErrorAlert from "../common/ErrorAlert";
import StatCard from "../common/StatCard";
import DataTable, { Column, EmptyState } from "../common/DataTable";
import Badge from "../common/Badge";
import { Form } from "../../types";

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const {
    data: formsData,
    isLoading,
    error,
  } = useQuery("forms", () => formsApi.getForms(0, 5, "created_at", "desc", true));

  const forms = formsData?.items || [];
  const stats = formsData?.stats;

  const columns: Column<Form>[] = useMemo(() => [
    {
      key: 'name',
      header: t("dashboard.recentForms.name"),
      render: (form) => (
        <span className="text-sm font-medium text-gray-900">
          {form.name}
        </span>
      ),
    },
    {
      key: 'status',
      header: t("dashboard.recentForms.status"),
      render: (form) => (
        <Badge
          label={form.is_active ? "Active" : "Inactive"}
          variant={form.is_active ? "success" : "neutral"}
        />
      ),
    },
    {
      key: 'variations',
      header: t("dashboard.recentForms.variations"),
      render: (form) => (
        <span className="text-sm text-gray-500">
          {form.variations.length} {t("dashboard.recentForms.variations")}
        </span>
      ),
    },
    {
      key: 'created',
      header: t("dashboard.recentForms.created"),
      render: (form) => (
        <span className="text-sm text-gray-500">
          {form.created_at
            ? new Date(form.created_at).toLocaleDateString()
            : "N/A"}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t("dashboard.recentForms.actions"),
      align: 'right',
      render: (form) => (
        <Link
          to={`/forms/${form.id}`}
          className="text-primary-600 hover:text-primary-900 text-sm font-medium"
        >
          {t("dashboard.recentForms.view")}
        </Link>
      ),
    },
  ], [t]);

  const emptyState: EmptyState = {
    icon: DocumentTextIcon,
    title: t("dashboard.recentForms.noForms"),
    description: t("dashboard.recentForms.noFormsDescription"),
    action: {
      label: t("dashboard.recentForms.createFirstForm"),
      component: 'link',
      to: '/forms/new',
    },
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorAlert message={t("dashboard.errorLoading")} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            {t("dashboard.title")}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {t("dashboard.subtitle")}
          </p>
        </div>
        <div className="mt-4 flex items-center space-x-4 md:mt-0 md:ml-4">
          <Link
            to="/forms/new"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            {t("dashboard.createForm")}
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={DocumentTextIcon}
          label={t("dashboard.stats.totalForms")}
          value={stats?.total_forms || 0}
          iconColor="text-gray-400"
        />
        <StatCard
          icon={EyeIcon}
          label={t("dashboard.stats.activeForms")}
          value={stats?.active_forms || 0}
          iconColor="text-gray-400"
        />
        <StatCard
          icon={ChartBarIcon}
          label={t("dashboard.stats.totalSubmissions")}
          value={stats?.total_submissions || 0}
          iconColor="text-gray-400"
        />
        <StatCard
          icon={ChartBarIcon}
          label={t("dashboard.stats.totalRevenue")}
          value={`$${(stats?.total_revenue || 0).toFixed(2)}`}
          iconColor="text-green-400"
        />
      </div>

      {/* Recent Forms */}
      <DataTable
        data={forms}
        columns={columns}
        keyExtractor={(form) => form.id!}
        title={t("dashboard.recentForms.title")}
        emptyState={emptyState}
        headerActions={
          <Link
            to="/forms"
            className="text-sm font-medium text-primary-600 hover:text-primary-500"
          >
            {t("dashboard.recentForms.viewAll")}
          </Link>
        }
      />
    </div>
  );
};

export default Dashboard;
